import { useEffect, useState } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const NEVER_KEY = 'pwaInstallNever'
const REMIND_AT_KEY = 'pwaInstallRemindAt'
const REMIND_DAYS = 30

function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  // iOS Safari
  return (window.navigator as { standalone?: boolean }).standalone === true
}

function shouldSuppress(): boolean {
  if (localStorage.getItem(NEVER_KEY) === '1') return true
  const remindAt = Number(localStorage.getItem(REMIND_AT_KEY) || 0)
  if (remindAt && Date.now() < remindAt) return true
  return false
}

export default function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (isStandalone()) return
    if (shouldSuppress()) return

    const onPrompt = (e: Event) => {
      e.preventDefault()
      setEvt(e as BeforeInstallPromptEvent)
      setVisible(true)
    }
    const onInstalled = () => {
      localStorage.removeItem(NEVER_KEY)
      localStorage.removeItem(REMIND_AT_KEY)
      setVisible(false)
      setEvt(null)
    }

    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  if (!visible || !evt) return null

  const handleYes = async () => {
    try {
      await evt.prompt()
      const choice = await evt.userChoice
      if (choice.outcome === 'accepted') {
        localStorage.removeItem(NEVER_KEY)
        localStorage.removeItem(REMIND_AT_KEY)
      } else {
        // User dismissed the native prompt — treat as "remind me later"
        localStorage.setItem(
          REMIND_AT_KEY,
          String(Date.now() + REMIND_DAYS * 24 * 60 * 60 * 1000),
        )
      }
    } finally {
      setVisible(false)
      setEvt(null)
    }
  }

  const handleNever = () => {
    localStorage.setItem(NEVER_KEY, '1')
    setVisible(false)
    setEvt(null)
  }

  const handleLater = () => {
    localStorage.setItem(
      REMIND_AT_KEY,
      String(Date.now() + REMIND_DAYS * 24 * 60 * 60 * 1000),
    )
    setVisible(false)
    setEvt(null)
  }

  return (
    <div
      className="install-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-title"
      onClick={handleLater}
    >
      <div className="install-card" onClick={(e) => e.stopPropagation()}>
        <h2 id="install-title" className="install-title">
          Install N73AA Logbook?
        </h2>
        <p className="install-body">
          Add the app to your home screen for faster access and a full-screen
          experience.
        </p>
        <div className="install-actions">
          <button
            type="button"
            className="install-btn install-btn-primary"
            onClick={handleYes}
          >
            Yes, install
          </button>
          <button
            type="button"
            className="install-btn"
            onClick={handleLater}
          >
            Remind me later
          </button>
          <button
            type="button"
            className="install-btn install-btn-muted"
            onClick={handleNever}
          >
            Never
          </button>
        </div>
      </div>
    </div>
  )
}
